import {PlausibleCartSource} from '@bitstillery/common/lib/utils'
import {classes, present} from '@bitstillery/common/lib/utils'
import {html_element} from '@bitstillery/common/lib/dom'
import {as_state} from '@bitstillery/common/lib/unchecked'
import {Changed, Country, DataCard, FieldCheckbox, Icon, Link} from '@bitstillery/common/components/portal'
import {MithrilComponent} from '@bitstillery/mithril'
import {to_specs_parts, to_tags} from '@bitstillery/common/lib/specs'
import {$t, config} from '@bitstillery/common/app'
import {format_money_responsive} from '@bitstillery/common/lib/format'
import m from '@bitstillery/mithril'
import {AvailabilityType, CartValidation} from '@bitstillery/common/api/fact2server'
import type {GetPortalOfferItemResponse} from '@bitstillery/common/api/fact2server'
import {format_delivery_period} from '@bitstillery/common/lib/format'
import {category_key} from '@/lib/category_key'
import {ProductQuantity} from './quantity/quantity'
import {ProductImage} from './image/image'
import {offer_card_layout_data_attr, resolve_portal_offer_card_layout} from './product_card_layout'
import {Tippy} from '@bitstillery/common/components/ui/tip/tip'

export type {PortalOfferProductCardLayout} from './product_card_layout'

import type {CollectionProxy} from '@bitstillery/common/lib/collection'
import {scroll_render} from '@bitstillery/common/lib/collection'

import {require_login} from '@/components/auth/require_login'
import {quantity_registry} from '@/lib/quantity_registry'
import {$m, $s} from '@/app'
import {OfferItemAmount} from '@/components/lib/amount/amount'
import type {CartOfferItem} from '@/models/cart'

export function to_cpp_cpl(
    offer_item: Pick<GetPortalOfferItemResponse, 'case_number_of_bottles' | 'cases_per_pallet' | 'cases_per_pallet_layer'>,
) {
    const strings: string[] = []
    const show_btls_str = $s.cart.show_bottles ? 'btl' : 'cs'
    const btl_multiplier = $s.cart.show_bottles ? offer_item.case_number_of_bottles : 1
    if (offer_item.cases_per_pallet) {
        strings.push(`Pallet: ${offer_item.cases_per_pallet * btl_multiplier} ${show_btls_str}`)
    }
    if (offer_item.cases_per_pallet_layer) {
        strings.push(`Layer: ${offer_item.cases_per_pallet_layer * btl_multiplier} ${show_btls_str}`)
    }

    return strings.join(' / ')
}

/** The spec segments rendered on one line with muted middot separators, so the values (volume, ABV, codes)
 *  scan cleanly instead of drowning in slashes. Empty string when there are no specs (so the row drops out). */
function to_specs_line(offer_item: Parameters<typeof to_specs_parts>[0]): m.Children {
    const parts = to_specs_parts(offer_item)
    if (parts.length === 0) {
        return ''
    }
    return (
        <span className='spec-line'>
            {parts.map((part, index) => [
                index > 0 ? <span className='spec-sep'>·</span> : null,
                <span className='spec-part'>{part}</span>,
            ])}
        </span>
    )
}

/** Offer cards (grid and list) show at most this many metadata rows — they must fit the reserved
 *  spec strip without scrolling (traders rejected a spec scrollbar); the full spec opens from the
 *  title click ({@link OfferItemDetailDialog}). */
export const MAX_LIST_DETAIL_ENTRIES = 5

export function build_offer_card_detail_entries(args: {
    bottle_gtin: string[]
    offer_item: CartOfferItem
    raw_offer_item: Parameters<typeof to_tags>[0]
}) {
    const {bottle_gtin, offer_item, raw_offer_item} = args
    const product_category = category_key(offer_item.product_category)
    const spec_value = to_specs_line(offer_item)
    const rows = [
        {label: <Icon name='clipboardText' size='s' />, value: spec_value},
        {
            label: <Icon name='tagMultiple' size='s' tip={() => to_tags(raw_offer_item)} />,
            value: to_tags(raw_offer_item) as string | undefined,
        },
        {label: <Icon name='gtin' size='s' />, value: bottle_gtin.join(', ')},
        {
            label: <Icon name='weight' size='s' tip={$t('product.weight_tip')} />,
            value: offer_item.case_weight ? `${Math.round(Number(offer_item.case_weight) * 100) / 100} kg` : '',
        },
        {
            label: <Icon name='cpp' size='s' tip={$t('product.cpp_cpl_tip')} />,
            value: to_cpp_cpl(offer_item),
        },
        {
            label: <Icon name='bottle' size='s' />,
            value: product_category ? $t(`product_category.${product_category}`) : '',
        },
    ]
    return rows.filter((i) => (Array.isArray(i.value) ? i.value.length > 0 : i.value))
}

export const STATUS_ICONS = {
    [AvailabilityType.Purchase]: {
        icon: 'truck',
        type: 'info',
    },
    [AvailabilityType.Stock]: {
        icon: 'checkboxMarked',
        type: 'success',
    },
    [AvailabilityType.Tbo]: {
        icon: 'tbo',
        type: 'surface',
    },
    [AvailabilityType.Unavailable]: {
        icon: 'close',
        type: 'danger',
    },
} as const

export type StatusIconEntry = (typeof STATUS_ICONS)[AvailabilityType]

/** Safe lookup: unknown or missing {@link AvailabilityType} falls back to {@link AvailabilityType.Unavailable}. */
export function status_icon_entry_for_type(offer_item_type: AvailabilityType | string | undefined | null): StatusIconEntry {
    if (offer_item_type == null) {
        return STATUS_ICONS[AvailabilityType.Unavailable]
    }
    const entry = (STATUS_ICONS as Record<string, StatusIconEntry | undefined>)[offer_item_type]
    return entry ?? STATUS_ICONS[AvailabilityType.Unavailable]
}

export interface CollectionProductAttrs {
    collection: CollectionProxy
    /** Href for the title + specs block, deep-linking to where this card's collection lives in the shop
     *  (e.g. the CMS carousel's offer filter). Omitted: that block isn't a link. */
    details_link?: string | undefined
    global_index: number // Global index in the total collection for odd/even styling
    /** Null for a row the collection hasn't loaded yet, which renders as a placeholder. */
    offer_item: CartOfferItem | null
    /** When true, show placeholders for price/stock/cart (for non-authenticated users) */
    guest?: boolean
    /** Called when item height changes (e.g. for virtualized lists) */
    on_item_height_change?: ((global_index: number, height_px: number) => void) | undefined
    source?: PlausibleCartSource
    variant: 'grid' | 'list' | 'cart' | 'offer'
    virtualize: boolean
}

/** What `CollectionItems` hands an item renderer next to the row. */
export interface CollectionItemOptions {
    global_index: number
    on_item_height_change?: CollectionProductAttrs['on_item_height_change']
}

function navigate_shop_offer_detail(offer_item: Pick<CartOfferItem, 'artkey' | 'offer_item_artkey'>) {
    const artkey = offer_item.offer_item_artkey ?? offer_item.artkey
    const route = m.route.get()
    const qi = route.indexOf('?')
    const path = qi >= 0 ? route.slice(0, qi) : route
    const q = new URLSearchParams(qi >= 0 ? route.slice(qi + 1) : '')
    q.set('offer_detail', String(artkey))
    const qs = q.toString()
    void m.route.set(qs ? `${path}?${qs}` : path)
}

export class CollectionProduct extends MithrilComponent<CollectionProductAttrs> {
    element: HTMLElement | null = null
    was_placeholder = false // Track if this was a placeholder in previous render
    private resize_observer: ResizeObserver | null = null
    private virtual_row_index = 0
    private on_height_change: CollectionProductAttrs['on_item_height_change']

    override onbeforeupdate(vnode: m.Vnode<CollectionProductAttrs>, old: m.Vnode<CollectionProductAttrs>) {
        // Skip the redundant re-render during a scroll-driven window redraw: only the scroll offset moved,
        // not this card's data. Fine-grained signal tracking still re-renders the card via a targeted redraw
        // whenever anything it reads actually changes, so this only drops work that would produce no change.
        // Same item object + same slot ⇒ nothing here can differ.
        if (
            scroll_render.active &&
            vnode.attrs.offer_item === old.attrs.offer_item &&
            vnode.attrs.global_index === old.attrs.global_index
        ) {
            return false
        }
        this.virtual_row_index = vnode.attrs.global_index
        this.on_height_change = vnode.attrs.on_item_height_change
        return true
    }

    override oncreate(vnode: m.VnodeDOM<CollectionProductAttrs>) {
        this.element = html_element(vnode.dom)
        this.virtual_row_index = vnode.attrs.global_index
        this.on_height_change = vnode.attrs.on_item_height_change
        if (vnode.attrs.virtualize) {
            // Items.tsx already handles cache population synchronously before rendering
            this.was_placeholder = !vnode.attrs.offer_item
            this.resize_observer = new ResizeObserver(() => {
                const el = this.element
                if (!el || !this.on_height_change) {
                    return
                }
                // A placeholder is a fixed-height stand-in, not the row: it keeps the base card height while
                // a real shop card sizes to its content (~30px shorter). Reporting it teaches the virtualiser
                // a height no real row ever has, and the spacer then lurches by that much the moment the row
                // is scrolled back into — read from the DOM so this can't drift from what was laid out.
                if (el.classList.contains('placeholder')) {
                    return
                }
                const h = el.getBoundingClientRect().height
                this.on_height_change(this.virtual_row_index, h)
            })
            this.resize_observer.observe(vnode.dom)
        }
    }

    override onremove() {
        this.resize_observer?.disconnect()
        this.resize_observer = null
    }

    override onupdate(vnode: m.VnodeDOM<CollectionProductAttrs>) {
        if (!vnode.attrs.virtualize) {
            return
        }

        // Detect transition from placeholder to real item and re-trigger animation
        // Items.tsx already handles cache population synchronously before rendering
        const is_placeholder_now = !vnode.attrs.offer_item
        if (this.element && this.was_placeholder && !is_placeholder_now) {
            // Placeholder became real item - re-trigger fade-in animation
            this.element.style.animation = 'none'
        }
        this.was_placeholder = is_placeholder_now
    }

    private render_placeholder(vnode: m.Vnode<CollectionProductAttrs>) {
        // Determine odd/even based on global index (1-based for styling)
        const is_even = (vnode.attrs.global_index + 1) % 2 === 0
        const is_odd = (vnode.attrs.global_index + 1) % 2 === 1

        return (
            // Same root key as the loaded card so the placeholder→real transition patches in place
            // instead of recreating the root (and its just-loaded image).
            <div
                className={classes('c-collection-product placeholder', {even: is_even, odd: is_odd})}
                key={vnode.attrs.global_index}
            >
                <div className='block-image' />
                <div className='block-details' />
                <div className='block-order' />
            </div>
        )
    }

    private render_guest(vnode: m.Vnode<CollectionProductAttrs>, offer_item: CartOfferItem) {
        const is_even = (vnode.attrs.global_index + 1) % 2 === 0
        const is_odd = (vnode.attrs.global_index + 1) % 2 === 1
        const category_img_src =
            offer_item.product_category_image_location && config.product_photo_host
                ? `${config.product_photo_host}/${offer_item.product_category_image_location}`
                : null

        // Guest: only pack size, volume, alcohol (no REF, T2, category, pallet config, deal label)
        const spec_item = {
            case_number_of_bottles: offer_item.case_number_of_bottles,
            bottle_volume: offer_item.bottle_volume,
            bottle_alcohol_percentage: offer_item.bottle_alcohol_percentage,
        }
        const spec_value = to_specs_line(spec_item)
        const spec_entries = spec_value ? [{label: <Icon name='clipboardText' size='s' />, value: spec_value}] : []

        return (
            <button
                className={classes('c-collection-product c-collection-product--guest', {
                    even: is_even,
                    odd: is_odd,
                })}
                key={vnode.attrs.global_index}
                type='button'
                onclick={() => require_login(`/shop?offer_item_artkeys=${offer_item.offer_item_artkey ?? offer_item.artkey}`)}
            >
                <div className='guest-spotlight-visual'>
                    <div className='block-image c-product-image'>
                        {category_img_src ? (
                            <img loading='lazy' src={category_img_src} alt={offer_item.product_name} />
                        ) : (
                            <Icon className='image-placeholder' name='bottlePlaceholder' type='unset' />
                        )}
                    </div>
                    <div className='guest-spotlight-glass'>
                        <div className='block-details'>
                            <div className='details-name'>
                                <span>{offer_item.product_name}</span>
                            </div>
                            {spec_entries.length > 0 && (
                                <DataCard
                                    className='details-specs'
                                    model={{data: spec_entries}}
                                    type='unset'
                                    variant='extra-dense'
                                />
                            )}
                        </div>
                        <div className='block-order'>
                            <div className='order-price'>
                                <div className='pricing pricing--guest'>
                                    <span className='guest-cta'>{$t('registration.login_to_see_price')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </button>
        )
    }

    view(vnode: m.Vnode<CollectionProductAttrs>) {
        if (!vnode.attrs.offer_item) {
            return this.render_placeholder(vnode)
        }

        if (vnode.attrs.guest) {
            return this.render_guest(vnode, vnode.attrs.offer_item)
        }

        if (!(vnode.attrs.offer_item.case_artkey in $s.cart.offer_items)) {
            $s.cart.offer_items[vnode.attrs.offer_item.case_artkey] = as_state(vnode.attrs.offer_item)
        }

        const offer_item = present($s.cart.offer_items[vnode.attrs.offer_item.case_artkey], 'cart offer item')
        // Read once: every level of a `$s.a.b.c` chain is its own proxy trap, signal access and
        // dependency record, and this view used the same three-deep path nine times. Hoisting collapses
        // those to one — the dependency is still registered by this read, so reactivity is unchanged.
        // Measured in isolation (mithril bench/scenarios/store-hot-path.ts): 2.6x for a repeated deep
        // path. It matters here because this component renders once per product card.
        const price_preference = $s.identity.user.price_preference

        // Full render - registry.get_or_create is already cached in a map
        const context = quantity_registry.get_or_create(vnode.attrs.offer_item.case_artkey, vnode.attrs.offer_item)
        // Captured so the stock tip below keeps the null check it renders under.
        const stock_units = context.data._stock_units
        const offer_item_price = $m.offer.offer_item_price(offer_item, $s.cart.errors)
        const availability_status_icon = status_icon_entry_for_type(offer_item.offer_item_type)

        const bottle_gtin = offer_item.bottle_gtin_code ?? []

        // Determine odd/even based on global index (1-based for styling)
        const is_even = (vnode.attrs.global_index + 1) % 2 === 0
        const is_odd = (vnode.attrs.global_index + 1) % 2 === 1

        const offer_card_layout = resolve_portal_offer_card_layout(vnode.attrs)
        /** Grid: fewer spec rows; list: full metadata (container query handles row density in CSS). */
        const is_offer_card_vertical = offer_card_layout === 'vertical'
        const is_offer_card_horizontal = offer_card_layout === 'horizontal'
        // The shop's list card already spends its title click on the full-spec dialog, so a deep link only
        // applies where nothing else claims that click (the CMS carousel's cards).
        const details_link = is_offer_card_horizontal ? undefined : vnode.attrs.details_link

        let origin_nodes: m.Children = null
        {
            const countries = $s.countries_of_origin[offer_item.artkey]
            if (Array.isArray(countries)) {
                const [only_country] = countries
                if (only_country && countries.length === 1) {
                    origin_nodes = (
                        <Country
                            country_code={only_country.country_of_origin}
                            tip={$t('product.stock_origin', {country: only_country.country_of_origin})}
                        />
                    )
                } else if (countries.length > 1) {
                    origin_nodes = countries.map((country) => (
                        <Country
                            country_code={country.country_of_origin}
                            tip={$t('product.stock_origin_count', {
                                count: $s.cart.show_bottles
                                    ? country.number_of_cases_available * offer_item.case_number_of_bottles
                                    : country.number_of_cases_available,
                                units: $t(`unit.${price_preference}`, {
                                    count: country.number_of_cases_available,
                                }),
                            })}
                        />
                    ))
                }
            }
        }

        const detail_data = build_offer_card_detail_entries({
            bottle_gtin,
            offer_item,
            raw_offer_item: vnode.attrs.offer_item,
        }).slice(0, MAX_LIST_DETAIL_ENTRIES)

        const details_children = [
            <div
                className='details-name'
                {...(is_offer_card_horizontal
                    ? {
                          onkeydown: (e: KeyboardEvent) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  navigate_shop_offer_detail(offer_item)
                              }
                          },
                          onclick: () => navigate_shop_offer_detail(offer_item),
                          role: 'button',
                          tabIndex: 0,
                      }
                    : {})}
            >
                <Tippy content={offer_item.product_name} delay={[400, 0]}>
                    {offer_item.product_name}
                </Tippy>
                {origin_nodes}
            </div>,
            <DataCard className='details-specs' model={{data: detail_data}} type='unset' variant='extra-dense' />,
        ]

        return (
            <div
                className={classes('c-collection-product', {
                    cached: vnode.attrs.collection.is_item_cached(vnode.attrs.global_index),
                    'c-collection-product--offer-card-horizontal': is_offer_card_horizontal,
                    'c-collection-product--offer-card-vertical': is_offer_card_vertical,
                    even: is_even,
                    expired: vnode.attrs.collection.is_item_cached_expired(vnode.attrs.global_index),
                    odd: is_odd,
                })}
                data-artkey={String(offer_item.artkey)}
                data-offer-card-layout={offer_card_layout_data_attr(offer_card_layout)}
                key={vnode.attrs.global_index}
            >
                <ProductImage className='block-image' key={vnode.attrs.global_index} offer_item={offer_item}>
                    {$s.offers.export.active && (
                        <FieldCheckbox
                            className='select-toggle'
                            computed={() => {
                                if (vnode.attrs.collection.state.selection.all) {
                                    return !vnode.attrs.collection.state.selection.ids.includes(offer_item.artkey)
                                }
                                return vnode.attrs.collection.state.selection.ids.includes(offer_item.artkey)
                            }}
                            key={vnode.attrs.global_index}
                            onclick={(e?: Event) => {
                                e?.stopPropagation()
                            }}
                            onAfterChange={(newValue) => {
                                if (!newValue) {
                                    vnode.attrs.collection.item_select(offer_item)
                                } else {
                                    vnode.attrs.collection.item_deselect(offer_item)
                                }
                            }}
                        />
                    )}
                </ProductImage>

                {details_link ? (
                    <Link
                        className='block-details'
                        href={details_link}
                        key={vnode.attrs.global_index}
                        tabindex={0}
                        type='default'
                    >
                        {details_children}
                    </Link>
                ) : (
                    <div className='block-details' key={vnode.attrs.global_index}>
                        {details_children}
                    </div>
                )}

                <div className='block-order' key={vnode.attrs.global_index}>
                    <div className='order-price'>
                        <div className='pricing'>
                            {(() => {
                                if (offer_item_price.current === 0) {
                                    return null
                                }

                                const is_discount = $m.offer.DISCOUNT_TYPES.includes(offer_item.offer_type)
                                const show_changed = (is_discount && offer_item_price.is_down) || offer_item_price.is_invalid

                                const total = (
                                    <OfferItemAmount
                                        currency={offer_item.currency}
                                        offer_item={offer_item}
                                        value={offer_item_price.current}
                                    />
                                )

                                const changed_total = (
                                    <Changed
                                        currency={offer_item.currency}
                                        current={offer_item_price.current}
                                        previous={offer_item_price.previous}
                                        raise_warning={(() => {
                                            const priceRaisedErrors = $s.cart.errors[CartValidation.PRICE_WAS_RAISED]
                                            return offer_item.case_artkey in priceRaisedErrors
                                        })()}
                                        raise_warning_tip={(() => {
                                            const priceRaisedErrors = $s.cart.errors[CartValidation.PRICE_WAS_RAISED]
                                            const error = priceRaisedErrors[offer_item.case_artkey]
                                            if (error) {
                                                return $t('cart.error.PRICE_WAS_RAISED', {
                                                    invalid_value: format_money_responsive(
                                                        error.invalid_value,
                                                        false,
                                                        offer_item.currency,
                                                    ),
                                                    valid_value: format_money_responsive(
                                                        error.valid_value,
                                                        false,
                                                        offer_item.currency,
                                                    ),
                                                })
                                            }
                                            return undefined
                                        })()}
                                        type={offer_item_price.is_invalid ? 'warning' : 'brand'}
                                        validity={offer_item_price.is_invalid ? null : offer_item.validity_date}
                                    />
                                )

                                return show_changed ? changed_total : total
                            })()}

                            {$s.identity.user.show_excise && offer_item_price.excise > 0.01 && (
                                <div className='excise'>
                                    <span>
                                        {$s.identity.user.include_excise_in_price
                                            ? $t('excise.including', {
                                                  amount: format_money_responsive(
                                                      offer_item_price.excise,
                                                      false,
                                                      offer_item.currency,
                                                  ),
                                              })
                                            : $t('excise.excluding', {
                                                  amount: format_money_responsive(
                                                      offer_item_price.excise,
                                                      false,
                                                      offer_item.currency,
                                                  ),
                                              })}
                                    </span>
                                </div>
                            )}
                        </div>

                        <ProductQuantity
                            mode='full'
                            offer_item={offer_item}
                            source={vnode.attrs.source ?? PlausibleCartSource.CART}
                        />
                    </div>

                    <div className='order-availability'>
                        <div className='availability-status'>
                            <Icon size='s' name={availability_status_icon.icon} type={availability_status_icon.type} />
                            {(() => {
                                if (offer_item.offer_item_type === AvailabilityType.Tbo) {
                                    return `${$t('product.availability.tbo', {period: format_delivery_period(offer_item.delivery_period)})}`
                                } else if (offer_item.offer_item_type === AvailabilityType.Purchase) {
                                    return `${$t('product.availability.eta', {period: format_delivery_period(offer_item.expected_entry_date_in_weeks)})}`
                                } else if (offer_item.offer_item_type === AvailabilityType.Stock) {
                                    return $t('product.availability.stock')
                                }
                                return $t('product.availability.unavailable')
                            })()}
                        </div>
                        <div className='availability-cart'>
                            {stock_units !== null && (
                                <div className='in-stock'>
                                    <Icon
                                        name='stock'
                                        size='s'
                                        tip={() => {
                                            let tip = ''

                                            if (context.data._out_of_stock) {
                                                tip = $t('cart.unavailable')
                                                if (context.data._cart_cases > 0) {
                                                    return `${$t('cart.unavailable')} ${$t('cart.unavailable_action')}`
                                                }
                                                return tip
                                            } else if (context.data._less_stock_available) {
                                                const cart_content = $t('cart.content', {
                                                    count: context.data._cart_units,
                                                    units: $t(`unit.${price_preference}`, {
                                                        count: context.data._cart_units,
                                                    }),
                                                })
                                                const less_quantity = $t('cart.less_available', {
                                                    count: stock_units,
                                                    units: $t(`unit.${price_preference}`, {
                                                        count: stock_units,
                                                    }),
                                                })

                                                return `${cart_content} ${less_quantity} ${$t('cart.error_category.action.QUANTITY')}`
                                            } else if (context.data._step_size_quantity_invalid_cart) {
                                                return $t('cart.STEP_SIZE', {
                                                    count: context.data._step_size_unit,
                                                    min_count: context.data._min_quantity_unit,
                                                    units: $t(`unit.${price_preference}`, {
                                                        count: context.data._step_size_unit,
                                                    }),
                                                })
                                            }
                                            return $t('cart.stock', {
                                                count: stock_units,
                                                units: $t(`unit.${price_preference}`, {
                                                    count: stock_units,
                                                }),
                                            })
                                        }}
                                        type={(() => {
                                            const quantityErrors = $s.cart.errors[CartValidation.QUANTITY]
                                            if (offer_item.case_artkey in quantityErrors) {
                                                return 'warning'
                                            }
                                            return 'default'
                                        })()}
                                    />
                                    <span>
                                        {stock_units} {$s.cart.show_bottles ? 'btl' : 'cs'}
                                    </span>
                                </div>
                            )}
                            {(context.data._min_quantity_active || context.data._step_size_quantity_active) && (
                                <div className='moq'>
                                    <Icon
                                        name='quantity_constraint'
                                        size='s'
                                        tip={() => {
                                            if (
                                                context.data._min_quantity_invalid_cart ||
                                                context.data._min_quantity_invalid_modelvalue
                                            ) {
                                                return `${$t('cart.min_quantity', {
                                                    count: context.data._min_quantity_unit,
                                                    units: $t(`unit.${price_preference}`, {
                                                        count: context.data._min_quantity_unit,
                                                    }),
                                                })} ${$t('cart.error_category.action.QUANTITY')}`
                                            }

                                            if (
                                                context.data._step_size_quantity_invalid_cart ||
                                                context.data._step_size_quantity_invalid_modelvalue
                                            ) {
                                                const next_valid_unit = context.find_next_valid_units(context.data._cart_units)

                                                return `${$t('cart.error.STEP_SIZE', {
                                                    count: 1,
                                                    invalid_value: context.data._cart_units,
                                                    step_size: context.data._step_size_unit,
                                                    units: $t(`unit.${price_preference}`, {
                                                        count: context.data._step_size_unit,
                                                    }),
                                                    valid_value: next_valid_unit,
                                                })} ${$t('cart.error_category.action.QUANTITY')}`
                                            }

                                            let tip = ''
                                            tip += ` ${$t('cart.quantity_constraints_apply', {
                                                min_count: context.data._min_quantity_unit,
                                                step_size: context.data._step_size_unit,
                                                units: $t(`unit.${price_preference}`, {
                                                    count: context.data._step_size_unit,
                                                }),
                                            })}`

                                            return tip
                                        }}
                                        type={(() => {
                                            if (context.data._quantity_invalid) {
                                                return 'warning'
                                            }
                                            return 'default'
                                        })()}
                                    />
                                    <span>
                                        {(() => {
                                            if (context.data._quantity_invalid) {
                                                if (
                                                    context.data._min_quantity_invalid_cart ||
                                                    context.data._min_quantity_invalid_modelvalue ||
                                                    context.data._step_size_quantity_invalid_modelvalue ||
                                                    context.data._step_size_quantity_invalid_cart
                                                ) {
                                                    const min_quantity = $m.offer.unit_amount(
                                                        context.data._min_quantity_cases,
                                                        offer_item.case_number_of_bottles,
                                                    )
                                                    if (context.data._step_size_quantity_active) {
                                                        if (min_quantity !== context.data._step_size_unit) {
                                                            return `${min_quantity}/${context.data._step_size_unit}`
                                                        }
                                                        return min_quantity
                                                    }
                                                    return context.data._min_quantity_unit
                                                }
                                                // This is the default case, which should never happen.
                                                return context.data._min_quantity_unit
                                            }
                                            if (context.data._min_quantity_active) {
                                                const min_quantity = $m.offer.unit_amount(
                                                    context.data._min_quantity_cases,
                                                    offer_item.case_number_of_bottles,
                                                )
                                                if (context.data._step_size_quantity_active) {
                                                    if (min_quantity !== context.data._step_size_unit) {
                                                        return `${min_quantity}/${context.data._step_size_unit}`
                                                    }
                                                }
                                                return min_quantity
                                            }
                                            // This is the default case, which should never happen.
                                            return context.data._min_quantity_unit
                                        })()}
                                    </span>
                                </div>
                            )}

                            {context.data._cart_units > 0 && (
                                <div className='in-cart'>
                                    <Icon
                                        name='cart'
                                        size='s'
                                        tip={() =>
                                            $t('cart.content', {
                                                count: context.data._cart_units,
                                                units: $t(`unit.${price_preference}`, {
                                                    count: context.data._cart_units,
                                                }),
                                            })
                                        }
                                        type='info'
                                    />
                                    {context.data._cart_units}
                                    {context.data._stock_units === null && <span>{$s.cart.show_bottles ? 'btl' : 'cs'}</span>}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}
